export const name = "TRIM";
export const summary = `Removes the specified leading and trailing Unicode code points or bytes from a \`STRING\` or \`BYTES\` value.`;
export const reference = `
## TRIM

Removes the specified leading and trailing Unicode code points or bytes from a \`STRING\` or \`BYTES\` value.

\`\`\`
TRIM(value_to_trim[, set_of_characters_to_remove])
\`\`\`

### Description

Takes a \`STRING\` or \`BYTES\` value to trim.

If the value to trim is a \`STRING\`, removes from this value all leading and trailing Unicode code points in \`set_of_characters_to_remove\`. The set of code points is optional. If it isn't specified, all whitespace characters are removed from the beginning and end of the value to trim.

If the value to trim is \`BYTES\`, removes from this value all leading and trailing bytes in \`set_of_characters_to_remove\`. The set of bytes is required.


### Return type

- \`STRING\` if \`value_to_trim\` is a \`STRING\` value.
- \`BYTES\` if \`value_to_trim\` is a \`BYTES\` value.


### Examples

In the following example, all leading and trailing whitespace characters are removed from \`item\` because \`set_of_characters_to_remove\` isn't specified.



\`\`\`
SELECT CONCAT('#', TRIM( ' apple '), '#') AS example

/*----------*
 | example |
 +----------+
 | #apple# |
 *----------*/
\`\`\`

In the following example, all leading and trailing \`*\` characters are removed from ' **_apple_**'.



\`\`\`
SELECT TRIM(' ***apple***', '*') AS example

/*---------*
 | example |
 +---------+
 | apple |
 *---------*/
\`\`\`

In the following example, all leading and trailing \`x\`, \`y\`, and \`z\` characters are removed from 'xzxapplexxy'.



\`\`\`
SELECT TRIM('xzxapplexxy', 'xyz') as example

/*---------*
 | example |
 +---------+
 | apple |
 *---------*/
\`\`\`

In the following example, examine how \`TRIM\` interprets characters as Unicode code-points. If your trailing character set contains a combining diacritic mark over a particular letter, \`TRIM\` might strip the same diacritic mark from a different letter.



\`\`\`
SELECT
  TRIM('abaW̊', 'Y̊') AS a,
  TRIM('W̊aba', 'Y̊') AS b,
  TRIM('abaŪ̊', 'Y̊') AS c,
  TRIM('Ū̊aba', 'Y̊') AS d

/*------+------+------+------*
 | a | b | c | d |
 +------+------+------+------+
 | abaW | W̊aba | abaŪ | Ūaba |
 *------+------+------+------*/
\`\`\`

In the following example, all leading and trailing \`b'n'\`, \`b'a'\`, \`b'\\xab'\`bytes are removed from \`item\`.



\`\`\`
SELECT b'apple', TRIM(b'apple', b'na\\xab') AS example

-- Note that the result of TRIM is of type BYTES, displayed as a base64-encoded string.
/*----------------------+------------------*
 | item | example |
 +----------------------+------------------+
 | YXBwbGU= | cHBsZQ== |
 *----------------------+------------------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/string_functions
`;
